<template>
	<div class="competency-chart-expanded" :class="{ 'competency-chart-expanded--modal': view }">
		<div class="competency-chart-expanded__body">
			<apexchart
				:width="'100%'"
				:height="chartHeight"
				:type="chartType"
				:options="finalChartOptions"
				:series="chartSeries"
			></apexchart>
		</div>
	</div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import ChartDataBuilder from '@/shared/chart-builder';

const chartBuilder = new ChartDataBuilder();

function created() {
	// Solo proceder si dataCompetences está disponible
	if (this.dataCompetences && Object.keys(this.dataCompetences).length > 0) {
		chartBuilder.type = this.dataCompetences.type;
		chartBuilder.series = this.dataCompetences.series;
		chartBuilder.categories = this.dataCompetences.categories;
		this.chart = chartBuilder.generatechartData();
	}
	// Usar 'auto' por defecto, se sobrescribe si la prop height está definida
	this.localChartHeight = 'auto';
}

function data() {
	return {
		chart: {},
		localChartHeight: 'auto',
	};
}

export default {
	name: 'card-dashboard-individual',
	data,
	created,
	components: {
		apexchart: VueApexCharts,
	},
	props: {
		dataCompetences: {
			type: Object,
			default() {
				return {};
			},
		},
		title: {
			type: String,
			default: '',
		},
		width: {
			type: [Number, String],
		},
		height: {
			type: [Number, String],
			default: undefined,
		},
		view: {
			type: Boolean,
			default: false,
		},
		predefinedOptions: {
			type: Object,
			default() {
				return null;
			},
		},
	},
	computed: {
		chartHeight() {
			// Si la prop height está definida, usarla
			// De lo contrario, usar localChartHeight
			if (this.height !== undefined && this.height !== null) {
				return this.height;
			}
			return this.localChartHeight;
		},
		hasPredefinedOptions() {
			return this.predefinedOptions && Object.keys(this.predefinedOptions).length > 0;
		},
		chartType() {
			if (this.hasPredefinedOptions) {
				return this.predefinedOptions.chart.type;
			}
			return this.chart.type || this.dataCompetences.type;
		},
		chartSeries() {
			if (this.hasPredefinedOptions) {
				return this.dataCompetences.series;
			}
			return this.chart.series;
		},
		finalChartOptions() {
			// Si hay opciones predefinidas, usarlas directamente con ajustes mínimos
			if (this.hasPredefinedOptions) {
				const options = { ...this.predefinedOptions };
				// Ajustar parentHeightOffset para evitar espacio extra no deseado
				options.chart = {
					...(this.predefinedOptions.chart || {}),
					parentHeightOffset: 0,
				};
				return options;
			}
			// Si no hay opciones predefinidas, usar las modernizadas
			return this.modernChartOptions;
		},
		modernChartOptions() {
			const baseOptions = this.chart.options || {};
			const chartType = this.chart.type || this.dataCompetences.type;

			// Solo aplicar mejoras cosméticas, preservar colores y datos originales
			const cosmeticStyles = {
				fontFamily: 'inherit',
				toolbar: { show: false },
				parentHeightOffset: 0,
			};

			// Construir options preservando todo lo original (sin sobrescribir height)
			const enhancedOptions = {
				chart: {
					...(baseOptions.chart || {}),
					...cosmeticStyles,
				},
			};

			// Agregar grid si no existe
			if (!baseOptions.grid) {
				enhancedOptions.grid = {
					show: true,
					borderColor: '#f1f5f9',
					strokeDashArray: 0,
					padding: {
						top: 20,
						right: 60,
						bottom: 20,
						left: 20,
					},
				};
			} else {
				enhancedOptions.grid = baseOptions.grid;
				// Agregar padding si no existe en el grid original
				if (!enhancedOptions.grid.padding) {
					enhancedOptions.grid.padding = {
						top: 20,
						right: 60,
						bottom: 20,
						left: 20,
					};
				} else {
					// Asegurar padding mínimo en el grid original
					if (!enhancedOptions.grid.padding.top || enhancedOptions.grid.padding.top < 20) {
						enhancedOptions.grid.padding.top = 20;
					}
					if (!enhancedOptions.grid.padding.right || enhancedOptions.grid.padding.right < 60) {
						enhancedOptions.grid.padding.right = 60;
					}
					if (!enhancedOptions.grid.padding.bottom || enhancedOptions.grid.padding.bottom < 20) {
						enhancedOptions.grid.padding.bottom = 20;
					}
					if (!enhancedOptions.grid.padding.left || enhancedOptions.grid.padding.left < 20) {
						enhancedOptions.grid.padding.left = 20;
					}
				}
			}

			// Mejorar tooltip si existe
			if (baseOptions.tooltip) {
				enhancedOptions.tooltip = {
					...baseOptions.tooltip,
					theme: 'light',
					style: {
						fontSize: '13px',
						fontFamily: 'inherit',
					},
				};
			} else {
				enhancedOptions.tooltip = {
					enabled: true,
					theme: 'light',
					style: {
						fontSize: '13px',
						fontFamily: 'inherit',
					},
				};
			}

			// Copiar todas las demás opciones originales
			const keysToCopy = ['plotOptions', 'dataLabels', 'xaxis', 'yaxis', 'stroke', 'fill', 'colors', 'labels', 'legend', 'dataProvider'];
			keysToCopy.forEach((key) => {
				if (baseOptions[key] !== undefined) {
					enhancedOptions[key] = baseOptions[key];
				}
			});

			// Aplicar estilos modernos específicos por tipo SOLO si no existen en el original
			switch (chartType) {
			case 'bar':
				// Preservar borderRadius original si existe
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.bar) {
					enhancedOptions.plotOptions.bar = {};
				}
				// Solo aplicar borderRadius si el original tiene 0
				if (!enhancedOptions.plotOptions.bar.borderRadius
					|| enhancedOptions.plotOptions.bar.borderRadius === 0) {
					enhancedOptions.plotOptions.bar.borderRadius = 10;
					enhancedOptions.plotOptions.bar.borderRadiusApplication = 'end';
				}
				// Preservar isFunnel del original
				if (baseOptions.plotOptions
					&& baseOptions.plotOptions.bar
					&& baseOptions.plotOptions.bar.isFunnel) {
					enhancedOptions.plotOptions.bar.isFunnel = true;
				}
				// Mejorar estilos de labels si existen
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (enhancedOptions.yaxis
					&& enhancedOptions.yaxis.labels
					&& !enhancedOptions.yaxis.labels.style) {
					enhancedOptions.yaxis.labels = {
						...enhancedOptions.yaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 600,
							colors: ['#334155'],
						},
					};
				}
				// Mejorar dataLabels style y formatter
				if (enhancedOptions.dataLabels
					&& enhancedOptions.dataLabels.enabled) {
					if (!enhancedOptions.dataLabels.style) {
						enhancedOptions.dataLabels.style = {
							fontSize: '9px',
							fontWeight: 600,
							colors: ['#334155'],
						};
					}
					// Usar formatter más corto que solo muestre el valor
					if (!enhancedOptions.dataLabels.formatter) {
						enhancedOptions.dataLabels.formatter = function formatterVal(val) {
							return val;
						};
					}
				}
				break;

			case 'singlebar':
				// Preservar configuración original específica de singlebar
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.bar) {
					enhancedOptions.plotOptions.bar = {};
				}
				// Solo aplicar borderRadius si el original tiene 0
				if (!enhancedOptions.plotOptions.bar.borderRadius
					|| enhancedOptions.plotOptions.bar.borderRadius === 0) {
					enhancedOptions.plotOptions.bar.borderRadius = 10;
					enhancedOptions.plotOptions.bar.borderRadiusApplication = 'end';
				}
				// Mejorar estilos de labels
				if (enhancedOptions.yaxis
					&& enhancedOptions.yaxis.labels
					&& !enhancedOptions.yaxis.labels.style) {
					enhancedOptions.yaxis.labels.style = {
						fontSize: '10px',
						fontWeight: 600,
						colors: ['#334155'],
					};
				}
				break;

			case 'area':
				// Mejorar stroke si existe
				if (enhancedOptions.stroke) {
					if (!enhancedOptions.stroke.width) {
						enhancedOptions.stroke.width = 4;
					}
				}
				// Agregar fill gradiente si no existe
				if (!enhancedOptions.fill) {
					enhancedOptions.fill = {
						type: 'gradient',
						gradient: {
							shadeIntensity: 1,
							opacityFrom: 0.5,
							opacityTo: 0.1,
							stops: [0, 90, 100],
						},
					};
				}
				// Mejorar estilos de labels
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (baseOptions.yaxis
					&& baseOptions.yaxis.labels
					&& !baseOptions.yaxis.labels.style) {
					enhancedOptions.yaxis = baseOptions.yaxis;
					enhancedOptions.yaxis.labels = {
						...baseOptions.yaxis.labels,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				} else if (!baseOptions.yaxis) {
					enhancedOptions.yaxis = {
						labels: {
							style: {
								fontSize: '9px',
								fontWeight: 500,
								colors: ['#64748b'],
							},
						},
					};
				}
				break;

			case 'line':
				// Mejorar stroke si existe
				if (enhancedOptions.stroke) {
					if (!enhancedOptions.stroke.width) {
						enhancedOptions.stroke.width = 4;
					}
					if (!enhancedOptions.stroke.curve) {
						enhancedOptions.stroke.curve = 'smooth';
					}
				}
				// Agregar markers si no existen
				if (!enhancedOptions.markers) {
					enhancedOptions.markers = {
						size: 5,
						strokeWidth: 0,
						hover: { size: 8 },
					};
				}
				// Mejorar estilos de labels
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (baseOptions.yaxis
					&& baseOptions.yaxis.labels
					&& !baseOptions.yaxis.labels.style) {
					enhancedOptions.yaxis = baseOptions.yaxis;
					enhancedOptions.yaxis.labels = {
						...baseOptions.yaxis.labels,
						style: {
							fontSize: '9px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				} else if (!baseOptions.yaxis) {
					enhancedOptions.yaxis = {
						labels: {
							style: {
								fontSize: '9px',
								fontWeight: 500,
								colors: ['#64748b'],
							},
						},
					};
				}
				break;

			case 'radar':
				// Para radar, preservar configuración original y solo mejorar estilos
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.radialBar) {
					enhancedOptions.plotOptions.radialBar = {};
				}
				if (!enhancedOptions.plotOptions.radialBar.hollow) {
					enhancedOptions.plotOptions.radialBar.hollow = {
						size: '60%',
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.track) {
					enhancedOptions.plotOptions.radialBar.track = {
						background: '#f1f5f9',
						strokeWidth: '97%',
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels) {
					enhancedOptions.plotOptions.radialBar.dataLabels = {};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels.name) {
					enhancedOptions.plotOptions.radialBar.dataLabels.name = {
						show: true,
						fontSize: '12px',
						color: '#64748b',
						fontWeight: 600,
					};
				}
				if (!enhancedOptions.plotOptions.radialBar.dataLabels.value
					|| !enhancedOptions.plotOptions.radialBar.dataLabels.value.fontSize) {
					enhancedOptions.plotOptions.radialBar.dataLabels.value = {
						fontSize: '18px',
						fontWeight: 700,
						color: '#0f172a',
						formatter: function formatterVal(val) {
							return `${Number(val).toFixed(1)}%`;
						},
					};
				}
				// Agregar legend si no existe
				if (!enhancedOptions.legend) {
					enhancedOptions.legend = {
						show: true,
						position: 'bottom',
						fontSize: '11px',
						fontWeight: 600,
						itemMargin: {
							horizontal: 8,
							vertical: 4,
						},
					};
				}
				break;

			case 'boxPlot':
				// Para boxPlot, preservar configuración original y mejorar estilos
				if (!enhancedOptions.plotOptions) {
					enhancedOptions.plotOptions = {};
				}
				if (!enhancedOptions.plotOptions.boxPlot) {
					enhancedOptions.plotOptions.boxPlot = {};
				}
				// Mejorar estilos de labels
				if (enhancedOptions.xaxis
					&& enhancedOptions.xaxis.labels
					&& !enhancedOptions.xaxis.labels.style) {
					enhancedOptions.xaxis.labels = {
						...enhancedOptions.xaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				}
				if (baseOptions.yaxis
					&& baseOptions.yaxis.labels
					&& !baseOptions.yaxis.labels.style) {
					enhancedOptions.yaxis = baseOptions.yaxis;
					enhancedOptions.yaxis.labels = {
						...baseOptions.yaxis.labels,
						style: {
							fontSize: '10px',
							fontWeight: 500,
							colors: ['#64748b'],
						},
					};
				} else if (!baseOptions.yaxis) {
					enhancedOptions.yaxis = {
						labels: {
							style: {
								fontSize: '10px',
								fontWeight: 500,
								colors: ['#64748b'],
							},
						},
					};
				}
				break;

			default:
				break;
			}

			// Ajustar leyendas para evitar superposición con labels
			if (enhancedOptions.legend) {
				// Si la leyenda existe, asegurarnos de que tenga buen posicionamiento
				if (!enhancedOptions.legend.offsetY) {
					enhancedOptions.legend.offsetY = 0;
				}
				if (!enhancedOptions.legend.offsetX) {
					enhancedOptions.legend.offsetX = 0;
				}
				// Si la leyenda está en bottom, aumentar padding inferior
				if (enhancedOptions.legend.position === 'bottom'
					|| !enhancedOptions.legend.position) {
					// Aumentar padding inferior del grid para dar espacio a la leyenda
					if (!enhancedOptions.grid.padding) {
						enhancedOptions.grid.padding = {};
					}
					if (!enhancedOptions.grid.padding.bottom || enhancedOptions.grid.padding.bottom < 25) {
						enhancedOptions.grid.padding.bottom = 30;
					}
				}
				// Si la leyenda está en top, aumentar padding superior
				if (enhancedOptions.legend.position === 'top') {
					if (!enhancedOptions.grid.padding) {
						enhancedOptions.grid.padding = {};
					}
					if (!enhancedOptions.grid.padding.top || enhancedOptions.grid.padding.top < 20) {
						enhancedOptions.grid.padding.top = 25;
					}
				}
			}

			return enhancedOptions;
		},
	},
	watch: {
		dataCompetences: {
			handler(newVal) {
				if (newVal && Object.keys(newVal).length > 0) {
					chartBuilder.type = newVal.type;
					chartBuilder.series = newVal.series;
					chartBuilder.categories = newVal.categories;
					this.chart = chartBuilder.generatechartData();
				}
			},
			deep: true,
		},
		height: {
			handler(newVal) {
				if (newVal !== undefined && newVal !== null) {
					this.localChartHeight = newVal;
				}
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.competency-chart-expanded {
	width: 100%;
	display: block;
	background: #ffffff;

	&--modal {
		height: 100%;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}

	&__body {
		width: 100%;
		display: block;
		padding: 0;
		overflow: hidden;
		background: #ffffff;

		.competency-chart-expanded--modal & {
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
		}
	}
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/card-dashboard-individual.vue